import React, { FC, ReactElement, JSXElementConstructor } from 'react';
import { Box, Typography, Stack, Button, Grid, TextFieldProps } from '@mui/material';
import dayjs from 'dayjs';
import DateInput from 'components/DateInput/DateInput';
import { RangePreset } from '../types';

interface CustomDateRangeSelectorProps {
  rangePreset: RangePreset;
  customDateRange: { startDate: string; endDate: string };
  onDateChange: (field: 'startDate' | 'endDate', value: string) => void;
  onQuickSelect: (preset: 'TODAY' | 'THIS_WEEK' | 'THIS_MONTH') => void;
  onApply: () => void;
  onCancel: () => void;
}

const CustomDateRangeSelector: FC<CustomDateRangeSelectorProps> = ({
  rangePreset,
  customDateRange,
  onDateChange,
  onQuickSelect,
  onApply,
  onCancel,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Quick Selection Buttons */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
          Quick Selection
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Button
            variant={rangePreset === 'TODAY' ? 'contained' : 'outlined'}
            onClick={() => onQuickSelect('TODAY')}
            size="small">
            Today
          </Button>
          <Button
            variant={rangePreset === 'THIS_WEEK' ? 'contained' : 'outlined'}
            onClick={() => onQuickSelect('THIS_WEEK')}
            size="small">
            This Week
          </Button>
          <Button
            variant={rangePreset === 'THIS_MONTH' ? 'contained' : 'outlined'}
            onClick={() => onQuickSelect('THIS_MONTH')}
            size="small">
            This Month
          </Button>
        </Stack>
      </Box>

      {/* Custom Date Range Selection */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
          Custom Date Range
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <DateInput
              label="Start Date"
              value={customDateRange.startDate}
              textChange={(value: string) => onDateChange('startDate', value)}
              onChange={function (): void {
                throw new Error('Function not implemented.');
              }}
              renderInput={function (props: TextFieldProps): ReactElement<any, string | JSXElementConstructor<any>> {
                throw new Error('Function not implemented.');
              }}
              field="startDate"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DateInput
              label="End Date"
              value={customDateRange.endDate}
              textChange={(value: string) => onDateChange('endDate', value)}
              onChange={function (): void {
                throw new Error('Function not implemented.');
              }}
              renderInput={function (props: TextFieldProps): ReactElement<any, string | JSXElementConstructor<any>> {
                throw new Error('Function not implemented.');
              }}
              field="endDate"
              size="small"
              minDate={dayjs(customDateRange.startDate)}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Apply Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onApply}>
          Apply
        </Button>
      </Box>
    </Box>
  );
};

export default CustomDateRangeSelector;
